import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import { connectScreenSocket } from '../services/screenSocketOwner';
import { WALLS } from '../components/Workshop/flowScreenConfig';
import AudioControls from '../components/Workshop/AudioControls';

const Page = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #05050a;
  color: #fff;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const Menu = styled.nav`
  width: 240px;
  flex-shrink: 0;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.4);
  border-right: 1px solid #1c1c26;
  padding: 12px 0;
`;

const BackLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px 14px;
  color: #666;
  font-size: 12px;
  text-decoration: none;
  border-bottom: 1px solid #1c1c26;
  margin-bottom: 8px;

  &:hover { color: #fff; }
`;

const ChapterButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-left: 3px solid ${p => (p.active ? '#f59e0b' : 'transparent')};
  color: ${p => (p.active ? '#fff' : 'rgba(255,255,255,0.6)')};
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.05); }
`;

const StateButton = styled.button`
  width: 100%;
  padding: 7px 16px 7px 34px;
  background: ${p => (p.active ? 'rgba(245, 158, 11, 0.12)' : 'transparent')};
  border: none;
  color: ${p => (p.active ? '#f59e0b' : 'rgba(255,255,255,0.45)')};
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.05); }
`;

const Stage = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-content: center;
  gap: 14px;
  padding: 14px;
  min-width: 0;
  overflow-y: auto;
`;

const Wall = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WallLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: center;
`;

/* Two screens side by side, as on the physical wall */
const WallScreens = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 4px;
`;

const ScreenBox = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #000;
  border: 1px solid #222;
  border-radius: 6px;
  overflow: hidden;
`;

/* Absolutely positioned: transforms are visual-only, so an in-flow 1920px
   iframe would inflate the grid track to full size (learned the hard way).
   Pointer events pass THROUGH to the screens on purpose: tapping a play-view
   screen is a real tap (steers the cube, etc.) — this is the present surface,
   not a thumbnail. */
const ScreenIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 1920px;
  height: 1080px;
  transform-origin: top left;
  transform: scale(${p => p.scale});
  border: none;
`;

/* Room health: green = the real wall screen is registered and live,
   red = nobody holds this code (the play view's own virtual previews are
   excluded from statuses server-side, so this reflects the actual room) */
const HealthDot = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => (p.$online ? '#00ff88' : '#ff4444')};
  box-shadow: 0 0 6px ${p => (p.$online ? 'rgba(0,255,136,0.7)' : 'rgba(255,68,68,0.8)')};
  ${p => !p.$online && 'animation: kuutio-health-blink 1.2s ease-in-out infinite;'}

  @keyframes kuutio-health-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
`;

const CodeBadge = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.7);
  color: #999;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'JetBrains Mono', monospace;
`;

const ControlBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 12px;
  background: #0b0b12;
  border-top: 1px solid #1c1c26;
`;

const TickButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 34px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => (p.primary ? '#2563eb' : '#16161f')};
  border: 1px solid ${p => (p.primary ? '#3b82f6' : '#2a2a36')};
  color: #fff;

  &:hover { background: ${p => (p.primary ? '#1d4ed8' : '#22222e')}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Position = styled.div`
  min-width: 220px;
  text-align: center;
  font-size: 13px;
  color: #888;

  strong { color: #fff; }
`;

const LiteToggle = styled.button`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$active ? '#4caf50' : '#333')};
  background: ${p => (p.$active ? 'rgba(76,175,80,0.15)' : '#16161f')};
  color: #ccc;
  font-size: 12px;
  cursor: pointer;
`;

/**
 * PlayView — rehearsal / presenting surface. All 8 wall screens as live
 * iframes in the real room layout (walls with side-by-side screens), a left
 * menu to jump chapters/states, and the tick control. The iframes are real
 * ScreenClients in virtual mode: they follow the same prepare→reveal protocol
 * as the physical walls, so transitions here are the real thing.
 */
const PlayView = () => {
  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get('instance') || searchParams.get('kid');

  const [steps, setSteps] = useState([]);
  const [statesByStep, setStatesByStep] = useState({});
  const [pos, setPos] = useState(null);
  const [ticking, setTicking] = useState(false);
  // Lo-res previews by default: one laptop simulates all 8 screens, so heavy
  // tilas (8x 360° video decode + 8x 8K textures) get downscaled variants.
  // Real wall screens never see this — they load without the lite flag.
  const [liteMode, setLiteMode] = useState(true);
  // When on, the audio-host preview iframe plays the workshop audio from THIS
  // laptop (one-laptop demos). Off by default so a facilitator's play view
  // never doubles the real room audio.
  const [localAudio, setLocalAudio] = useState(false);
  const [screenScale, setScreenScale] = useState(0.15);
  const [screenStatuses, setScreenStatuses] = useState({});
  const stageRef = useRef(null);

  const loadPosition = useCallback(() => {
    fetch(API_ENDPOINTS.FLOW_POSITION(instanceId))
      .then(res => (res.ok ? res.json() : null))
      .then(setPos)
      .catch(() => {});
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;
    fetch(API_ENDPOINTS.FLOW_STATE(instanceId))
      .then(res => res.json())
      .then(async (data) => {
        const allSteps = data.active ? (data.allSteps || []) : [];
        setSteps(allSteps);
        const entries = await Promise.all(allSteps.map(step =>
          fetch(API_ENDPOINTS.STEP_SCREEN_STATES(step.id))
            .then(res => res.json())
            .then(rows => [step.id, rows])
            .catch(() => [step.id, []])
        ));
        setStatesByStep(Object.fromEntries(entries));
      })
      .catch(() => {});
    loadPosition();
  }, [instanceId, loadPosition]);

  // The menu is built from one mount-time fetch, but tilat are edited outside
  // this tab — the setup script, the workshop editor, a second admin device.
  // A stale list shows the wrong tilat (or none) while the walls correctly
  // follow the server, which reads as "navigation works but the menu is empty".
  // Refetch the active step's tilat whenever the position moves.
  useEffect(() => {
    const stepId = pos?.stepId;
    if (!stepId) return;
    fetch(API_ENDPOINTS.STEP_SCREEN_STATES(stepId))
      .then(res => (res.ok ? res.json() : null))
      .then(rows => { if (rows) setStatesByStep(prev => ({ ...prev, [stepId]: rows })); })
      .catch(() => {});
  }, [pos?.stepId]);

  // Room health: poll real-screen statuses so a dropped wall is visible
  // from the moderator seat without walking over
  useEffect(() => {
    if (!instanceId) return undefined;
    const socket = connectScreenSocket(instanceId, 'play-health', 'play-view');
    const request = () => socket.emit('screen-status-request', { instanceId });
    const onStatuses = (data) => {
      const statuses = {};
      (data.screens || []).forEach(sc => { statuses[sc.code] = sc.isOnline; });
      setScreenStatuses(statuses);
    };
    const onStatus = (data) => {
      setScreenStatuses(prev => ({ ...prev, [data.code]: data.isOnline }));
    };
    socket.on('connect', request);
    if (socket.connected) request();
    socket.on('screen-statuses', onStatuses);
    socket.on('screen-status', onStatus);
    const interval = setInterval(request, 5000);
    return () => {
      clearInterval(interval);
      // The socket is shared: remove only these handlers (#1427)
      socket.off('connect', request);
      socket.off('screen-statuses', onStatuses);
      socket.off('screen-status', onStatus);
    };
  }, [instanceId]);

  // Scale iframes to the screen boxes
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => {
      const box = node.querySelector('[data-screen-box]');
      if (box) setScreenScale(box.offsetWidth / 1920);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const sendTick = async (body) => {
    setTicking(true);
    try {
      await fetch(API_ENDPOINTS.FLOW_TICK(instanceId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      loadPosition();
    } finally {
      setTicking(false);
    }
  };

  if (!instanceId) {
    return <Page><ControlBar>Lisää ?instance=&lt;id&gt; osoitteeseen</ControlBar></Page>;
  }

  const positionLabel = pos
    ? <>Vaihe <strong>{pos.stepIndex + 1}/{pos.totalSteps}</strong>
        {pos.totalStates > 1 && <> · Tila <strong>{pos.stateIndex + 1}/{pos.totalStates}</strong></>}
        {pos.slideIndex !== null && <> · Dia <strong>{pos.slideIndex + 1}/{pos.slideCount}</strong></>}</>
    : '—';

  return (
    <Page>
      <Body>
        <Menu>
          <BackLink href={`/admin/workshop?instance=${instanceId}`}>
            <ChevronLeft size={14} /> Takaisin työpajaan
          </BackLink>
          {steps.map((step, idx) => {
            const states = statesByStep[step.id] || [];
            const isActive = pos?.stepId === step.id;
            return (
              <React.Fragment key={step.id}>
                <ChapterButton active={isActive} onClick={() => sendTick({ stepId: step.id })}>
                  <span>{idx + 1}. {step.name}</span>
                  {states.length > 1 && <ChevronDown size={12} />}
                </ChapterButton>
                {isActive && states.length > 1 && states.map((state, sIdx) => (
                  <StateButton
                    key={state.id}
                    active={pos?.stateId === state.id}
                    onClick={() => sendTick({ stateId: state.id })}
                  >
                    {state.name || `Tila ${sIdx + 1}`}
                  </StateButton>
                ))}
              </React.Fragment>
            );
          })}
        </Menu>

        <Stage ref={stageRef}>
          {WALLS.map(wall => (
            <Wall key={wall.id}>
              <WallLabel>{wall.name}</WallLabel>
              <WallScreens>
                {wall.screens.map(code => (
                  <ScreenBox key={code} data-screen-box>
                    <CodeBadge>{code}</CodeBadge>
                    <HealthDot
                      $online={!!screenStatuses[code]}
                      title={screenStatuses[code] ? `${code}: näyttö linjoilla` : `${code}: ei näyttöä linjoilla`}
                    />
                    <ScreenIframe
                      src={`/screen/${code}?instance=${instanceId}&virtual=true${liteMode ? '&lite=1' : ''}${localAudio ? '&audio=1' : ''}`}
                      scale={screenScale}
                      title={`Play ${code}`}
                    />
                  </ScreenBox>
                ))}
              </WallScreens>
            </Wall>
          ))}
        </Stage>
      </Body>

      <ControlBar>
        <TickButton disabled={ticking} onClick={() => sendTick({ direction: 'previous' })}>
          <ChevronLeft size={20} /> Edellinen
        </TickButton>
        <Position>{positionLabel}</Position>
        <TickButton primary disabled={ticking} onClick={() => sendTick({ direction: 'next' })}>
          Seuraava <ChevronRight size={20} />
        </TickButton>
        <LiteToggle
          $active={liteMode}
          onClick={() => setLiteMode(!liteMode)}
          title="Kevyt esikatselu: matalampi resoluutio esikatselunäytöillä — oikeat näytöt eivät muutu"
        >
          {liteMode ? '🍃 Kevyt' : '💎 Täysi'}
        </LiteToggle>
        <LiteToggle
          $active={localAudio}
          onClick={() => setLocalAudio(!localAudio)}
          title="Toista työpajan äänet tältä laitteelta (yhden läppärin demo). Pois = vain oikea ääninäyttö soittaa."
        >
          {localAudio ? '🔊 Ääni tässä' : '🔇 Ääni salissa'}
        </LiteToggle>
        <AudioControls instanceId={instanceId} />
        <LiteToggle
          onClick={() => fetch(API_ENDPOINTS.FLOW_RELOAD_SCREENS(instanceId), { method: 'POST' }).catch(() => {})}
          title="Lataa kaikki näytöt uudelleen (esim. uuden version jälkeen). Näytöt palaavat automaattisesti nykyiseen tilaan."
        >
          ⟳ Päivitä näytöt
        </LiteToggle>
      </ControlBar>
    </Page>
  );
};

export default PlayView;
